import { NumValBase } from "../../../Utils/NumberValue";
import { TIMER } from "../../Gameplay";
import { RoundManager } from "../../Round/RoundManager";
import { Stadium } from "../../Stadium/Stadium";
import { eTargetState, TargetBase } from "../Targets/Target";
import { TargetSettingsBase } from "../TargetSettings";
import { eTargetType } from "../TargetType";
import { TargetControllerBase, TargetPropAnim } from "./BaseController";



export class MovingTargetPathSectionSettings {
    /**
     * Pozice bodu A v ramci brany (x: 0 = levy okraj brany, 1 = pravy okraj brany, y: 0 = spodni okraj, 1 = horni okraj)
     */
    pointX: number | NumValBase;
    /**
     * Pozice bodu A v ramci brany (x: 0 = levy okraj brany, 1 = pravy okraj brany, y: 0 = spodni okraj, 1 = horni okraj)
     */
    pointY: number | NumValBase;
    /**
     * Vzdalenost v ramci brany na X (x: 0 = levy okraj brany, 1 = pravy okraj brany, y: 0 = spodni okraj, 1 = horni okraj)
     */
    distanceX: number | NumValBase;
    /**
     * Vzdalenost v ramci brany na Y (x: 0 = levy okraj brany, 1 = pravy okraj brany, y: 0 = spodni okraj, 1 = horni okraj)
     */
    distanceY: number | NumValBase;
    /**
     * Ease funkce pro pohyb terce.
     */
    ease: (k: number) => number;

    static readonly FLAG_ANIM_POINT_A = 1;
    static readonly FLAG_ANIM_POINT_B = 2;

    private _flags: number;

    /**
     * (RO) True, pokud ma dojit k animaci zobrazeni/skryti u pointA
     */
    get animPointA(): boolean { return (this._flags & MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A) != 0; }
    /**
     * (RO) True, pokud ma dojit k animaci zobrazeni/skryti u pointB
     */
    get animPointB(): boolean { return (this._flags & MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B) != 0; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(pointX: number | NumValBase, pointY: number | NumValBase, distanceX: number | NumValBase, distanceY: number | NumValBase, ease: (k: number) => number = pc.Linear, flags: number = 0) {
        this.pointX = pointX;
        this.pointY = pointY;
        this.distanceX = distanceX;
        this.distanceY = distanceY;

        this.ease = ease;
        this._flags = flags;
    }
}



export class MovingTargetSettings extends TargetSettingsBase {
    /**
     * Tarc se bude pohybovat mezi body A<->B. Pokud nenastaveno tak je A->B
     */
    static readonly FLAG_YOYO_MOVE = 0x10000;

    /**
     * (RO) Nastaveni sekci cesty
     */
    get pathSections(): MovingTargetPathSectionSettings[] { return this._pathSections; }
    protected _pathSections: MovingTargetPathSectionSettings[];
    /**
     * (RO) Rychlost pohybu terce pri nejnizsi obtiznosti v units per second.
     */
    get minDifMoveSpeed(): number { return this._minDifMoveSpeed; }
    protected _minDifMoveSpeed: number;
    /**
     * (RO) Rychlost pohybu terce pri nejvyssi obtiznosti v units per second.
     */
    get maxDifMoveSpeed(): number { return this._maxDifMoveSpeed; }
    protected _maxDifMoveSpeed: number;
    /**
     * (RO) True, pokud pohyb po ceste je tam a zpet. False, pokud pohyb zacina v bode A prvniho useku a konci v bode B posledniho useku
     */
    get moveYoyo(): boolean { return (this._flags & MovingTargetSettings.FLAG_YOYO_MOVE) != 0; }
    protected _moveYoyo: boolean;
    /**
     * (RO) Nastaveni animace zobrazeni terce (0 = opacity, 1 = scale)
     */
    get showAnimSettings(): TargetPropAnim[] { return this._showAnimSettings; }
    protected _showAnimSettings: TargetPropAnim[];
    /**
     * (RO) Nastaveni animace skryti terce (0 = opacity, 1 = scale)
     */
    get hideAnimSettings(): TargetPropAnim[] { return this._hideAnimSettings; }
    protected _hideAnimSettings: TargetPropAnim[];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(type: eTargetType, scale: number, path: MovingTargetPathSectionSettings[], minDifMoveSpeed: number, maxDifMoveSpeed: number, flags?: number, showAnim?: TargetPropAnim[], hideAnim?: TargetPropAnim[]) {
        super(type, scale, flags);

        this._pathSections = path;
        this._minDifMoveSpeed = minDifMoveSpeed;
        this._maxDifMoveSpeed = maxDifMoveSpeed;
        this._showAnimSettings = showAnim;
        this._hideAnimSettings = hideAnim;
    }
}



class PathSection {
    /**
     * (RO) Nastaveni useku
     */
    get settings(): MovingTargetPathSectionSettings { return this._settings; }
    private _settings: MovingTargetPathSectionSettings;
    /**
     * Pozice bodu A v hernim svete
     */
    get pointA(): pc.Vec2 { return this._pointA; }
    private _pointA: pc.Vec2;
    /**
     * Pozice bodu B v hernim svete
     */
    get pointB(): pc.Vec2 { return this._pointB; }
    private _pointB: pc.Vec2;
    /**
     * (RO) Delka useku v units
     */
    get length(): number { return this._length; }
    private _length: number;
    /**
     * (RO) Progress (0 az 1) v ramci cesty, kdy zacina ophyb po tomto useku
     */
    get startProgress(): number { return this._startProgress; }
    private _startProgress: number;
    /**
     * (RO) Cas v ramci celkoveho casu na cestu, kdy zacina pohyb po tomto useku (ms)
     */
    get startTime(): number { return this._startTime; }
    private _startTime: number;
    /**
     * (RO) Jak dlouho trva pohyb po tomto useku (ms)
     */
    get timeLength(): number { return this._timeLen; }
    private _timeLen: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(controller: MovingTargetController, settings: MovingTargetPathSectionSettings, targetGoalHSpace: number, targetGoalVSpace: number) {
        this._settings = settings;

        // Vypocitat pozici bodu A a B v hernim svete
        let disX = NumValBase.getValue(settings.distanceX);
        let disY = NumValBase.getValue(settings.distanceY);

        if (RoundManager.instance.roundSetup.flippedHorizontally)
            disX = -disX;

        this._pointA = new pc.Vec2();
        controller.convertRatioPos2WorldPos(NumValBase.getValue(this._settings.pointX), NumValBase.getValue(this._settings.pointY), this._pointA);
        this._pointB = new pc.Vec2(this._pointA.x + disX * targetGoalHSpace, this._pointA.y + disY * targetGoalVSpace);

        // Vzdalenost mezi body A a B
        this._length = this._pointA.distance(this._pointB);
    }

    //-------------------------------------------------------------------------
    /**
     * 
     * @param sectionTime Cas, ve kterem zacna pohyb po useku z bodu A do bodu B
     * @param pathTotLen Celkova delka cesty
     * @param pathTotTime Celkovy cas na cestu (z bodu A prvniho useku do bodu B posledniho useku)
     * @returns Cas, ve kterem zacne pohyb na dalsim useku
     */
    //-------------------------------------------------------------------------
    init(sectionTime: number, pathTotLen: number, pathTotTime: number): number {
        // Progress cesty, na kterem usek zacina
        this._startProgress = sectionTime / pathTotTime;

        // Cas zahajeni pohybu po sekci
        this._startTime = sectionTime;

        // Pomer delky sekce vuci celkove delce cesty
        let secPathRatio = this._length / pathTotLen;

        // Cas na sdolani sekce
        this._timeLen = pathTotTime * secPathRatio;

        if (this._startTime + this._timeLen > pathTotTime)
            this._timeLen = pathTotTime - this._startTime;

        return sectionTime + this._timeLen;
    }

    //-------------------------------------------------------------------------
    /**
     * Vypocet pozice
     * @param progress 0 az 1
     * @param pos vysledna pozice
     */
    //-------------------------------------------------------------------------
    getPos(progress: number, pos: pc.Vec2): void {
        let pA = this._pointA;
        let pB = this._pointB;

        pos.x = pA.x + (pB.x - pA.x) * progress;
        pos.y = pA.y + (pB.y - pA.y) * progress;
    }

    //-------------------------------------------------------------------------
    /**
     * Vypocita pozici v case
     * @param pathTime 
     * @param pos 
     */
    //-------------------------------------------------------------------------
    getPosInTime(pathTime: number, pos: pc.Vec2, dir: number): void {
        let progress: number;

        if (dir > 0) {
            progress = this._settings.ease((pathTime - this._startTime) / this._timeLen);
        }
        else {
            progress = 1 - this._settings.ease((this._timeLen - (pathTime - this._startTime)) / this._timeLen);
        }

        this.getPos(progress, pos);
    }
}



export class MovingTargetPathPos {
    /**
     * Pozice terce
     */
    pos: pc.Vec2;
    /**
     * Index sekce cesty
     */
    sectionId: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        this.pos = new pc.Vec2();
    }
}



export class MovingTargetController extends TargetControllerBase {
    /**
     * Minimalni rychlost pohybu terce
     */
    private static readonly MIN_MOVE_SPEED = 0.5;
    /**
     * (RO) Celkova delka cesty v units
     */
    get pathLen(): number { return this._pathLen; }
    private _pathLen: number;
    /**
     * Celkovy cas na sdolani cesty z bodu A prvniho useku do bodu B posledniho useku
     */
    private _pathTimeLen: number;
    /**
     * (RO) Jednotlive sekce cesty
     */
    get pathSections(): PathSection[] { return this._pathSections; }
    private _pathSections: PathSection[] = [];
    /**
     * Index aktualni sekce
     */
    private _curPathSectionId: number;
    /**
     * Smer pohybu terce. 1 = z bodu A do bodu B, -1 = z bodu B do bodu A
     */
    private _curPathDir: number;

    private _animPropsVal: number[] = [];

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        super();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(target: TargetBase, settings: MovingTargetSettings): void {
        super.init(target, settings);

        let roundMng = RoundManager.instance;

        // Vytvorit jednotlive useky cesty podle nastaveni
        let hSpace = target.goalHSpace;
        let vSpace = target.goalVSpace;
        let pathLen = 0;

        this._pathSections = [];

        settings.pathSections.forEach((set: MovingTargetPathSectionSettings): void => {
            let pe = new PathSection(this, set, hSpace, vSpace);

            pathLen += pe.length;
            this._pathSections.push(pe);
        });

        this._pathLen = pathLen;

        // Rychlost pohybu v units per second
        let moveSpeed = settings.minDifMoveSpeed + (settings.maxDifMoveSpeed - settings.minDifMoveSpeed) * roundMng.roundDifficultyIncRatio;

        // Za kazdy pokus zpomalit o X%
        /*if (roundMng.roundAttemptId > 0) {
            moveSpeed -= moveSpeed * (0.15 * roundMng.roundAttemptId);

            if (moveSpeed < MovingTargetController.MIN_MOVE_SPEED)
                moveSpeed = MovingTargetController.MIN_MOVE_SPEED;
        }*/

        // Cas na prekonani vzdalenosti z bodu A prvniho useku do bodu B posledniho useku
        let pathTime = this._pathTimeLen = Math.round((pathLen / moveSpeed) * 1000);

        // Inicializovat jednotlive useky cesty
        let sectionTime = 0;

        this._pathSections.forEach((section: PathSection): void => {
            sectionTime = section.init(sectionTime, pathLen, pathTime);
        });

        this.restart();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    restart(): void {
        // Index aktualni sekce
        this._curPathSectionId = 0;

        // Reset animovanych hodnot
        for (let i = 0; i < 2; i++)
            this._animPropsVal[i] = 1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._target.state == eTargetState.idle) {
            let time = this.convertGameTimeToPathTime(TIMER.time);

            this.updatePathSection(time);

            // Pozice terce
            this.updatePosition(time);
            this._target.container.setPosition(this._pos.x, this._pos.y, Stadium.END_Z + this._target.id * TargetBase.Z_SPACING);

            // Animace zobrazeni/skryti terce
            this.updateAnimValues(time);

            this._target.opacity = this._animPropsVal[0];
            this._target.scale = this._animPropsVal[1];
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Ziskani pozice terce pro zadany progress cesty
     * @param pathProgress Progress cesty (0 az 1)
     * @param output 
     * @param secId Index sekce, od ktere se ma hledat
     */
    //-------------------------------------------------------------------------
    getPosition(pathProgress: number, output: MovingTargetPathPos, secId: number = 0): void {
        // Ziskat index sekce pro "pathProgress"
        let secProgressStart: number;
        let secProgressEnd: number;

        while (true) {
            secProgressStart = this._pathSections[secId].startProgress;

            if (pathProgress < secProgressStart) {
                secId--;
                continue;
            }

            secProgressEnd = secId < this._pathSections.length - 1 ? this._pathSections[secId + 1].startProgress : 1;

            if (pathProgress > secProgressEnd) {
                secId++;
                continue;
            }

            break;
        }

        // Ziskat pozici
        this._pathSections[secId].getPos((pathProgress - secProgressStart) / (secProgressEnd - secProgressStart), output.pos);
        output.sectionId = secId;
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci pozici terce v zadany cas
     * @param time Herni cas
     * @returns 
     */
    //-------------------------------------------------------------------------
    getPositionInTime(time: number): pc.Vec2 {
        let curPathDir = this._curPathDir;
        let curPathSecId = this._curPathSectionId;

        // Prepocitat na cas v ramci cesty
        time = this.convertGameTimeToPathTime(time);

        // Nastavit aktualni sekci
        this.updatePathSection(time);

        // Nastavit aktualni pozici
        let pos = new pc.Vec2();

        this._pathSections[this._curPathSectionId].getPosInTime(time, pos, this._curPathDir);

        this._curPathDir = curPathDir;
        this._curPathSectionId = curPathSecId;

        return pos;
    }

    //-------------------------------------------------------------------------
    /**
     * Prevod herniho casu na cas v ramci cesty
     * @param time 
     * @returns 
     */
    //-------------------------------------------------------------------------
    private convertGameTimeToPathTime(time: number): number {
        this._curPathDir = 1;        // Smer pohybu (1 = z bodu A do bodu B, -1 = z bodu B do bodu A)

        time = (time - this._target.stateTime);

        if ((this._target.settings as MovingTargetSettings).moveYoyo) {
            time %= (this._pathTimeLen * 2);

            if (time > this._pathTimeLen) {
                // Pohyb smerem zpet
                time = this._pathTimeLen - (time - this._pathTimeLen);
                this._curPathDir = -1;
            }
        }
        else {
            time %= this._pathTimeLen;
        }

        return time;
    }

    //-------------------------------------------------------------------------
    /**
     * Nastavi aktualni sekci cesty podle casu
     * @param time Cas v ramci cesty
     */
    //-------------------------------------------------------------------------
    private updatePathSection(time: number): void {
        let sec = this._pathSections[this._curPathSectionId];

        while (time < sec.startTime)
            sec = this._pathSections[--this._curPathSectionId];

        while (time > sec.startTime + sec.timeLength)
            sec = this._pathSections[++this._curPathSectionId];
    }

    //-------------------------------------------------------------------------
    /**
     * Aktualizace pozice terce podle zadaneho casu
     * @param time Cas v ramci cesty
     * @returns 
     */
    //-------------------------------------------------------------------------
    private updatePosition(time: number): void {
        // Ziskat pozici
        this._pathSections[this._curPathSectionId].getPosInTime(time, this._pos, this._curPathDir);
    }

    //-------------------------------------------------------------------------
    /**
     * Aktualizace animovanych hodnot terce podle zadaneho casu
     * @param time Cas v ramci cesty
     */
    //-------------------------------------------------------------------------
    private updateAnimValues(time: number): void {
        let settings = this._target.settings as MovingTargetSettings;
        let sec = this._pathSections[this._curPathSectionId];

        if (sec.settings.animPointA || sec.settings.animPointB) {
            for (let i = 0; i < this._animPropsVal.length; i++)
                this._animPropsVal[i] = undefined;

            time -= sec.startTime;

            if (this._curPathDir > 0) {
                // Pohyb A->B
                if (sec.settings.animPointA) {
                    settings.showAnimSettings.forEach((anim: TargetPropAnim, i: number): void => {
                        this._animPropsVal[i] = anim.getAnimVal(time);
                    });
                }

                if (sec.settings.animPointB) {
                    settings.hideAnimSettings.forEach((anim: TargetPropAnim, i: number): void => {
                        if (this._animPropsVal[i] == undefined)
                            this._animPropsVal[i] = anim.getAnimVal(time - (sec.timeLength - anim.time));
                    });
                }
            }
            else {
                // Pohyb B->A
                if (sec.settings.animPointB) {
                    settings.showAnimSettings.forEach((anim: TargetPropAnim, i: number): void => {
                        this._animPropsVal[i] = anim.getAnimVal(sec.timeLength - time);
                    });
                }

                if (sec.settings.animPointA) {
                    settings.hideAnimSettings.forEach((anim: TargetPropAnim, i: number): void => {
                        if (this._animPropsVal[i] == undefined)
                            this._animPropsVal[i] = anim.getAnimVal(anim.time - time)
                    });
                }
            }
        }

        // Nastaveni vychozich hodnot
        for (let i = 0; i < this._animPropsVal.length; i++)
            if (this._animPropsVal[i] == undefined)
                this._animPropsVal[i] = 1;
    }
}